<script setup lang="ts">
import {onMounted, shallowRef, useTemplateRef} from "vue";
import {ICellEditorParams} from "ag-grid-community";
import type * as monacoEditor from "monaco-editor";
import type {IEvent, IKeyboardEvent} from "monaco-editor";
import BigTextFieldWidgetUtil from "@/utils/editView/widgets/BigTextFieldWidgetUtil.ts";
import EditScreenGridHandler from "@/utils/common/EditScreenGridHandler.ts";
import {onClickOutside} from "@vueuse/core";
import AGGridUtils from "@/utils/UIConstants/AGGridUtils.ts";
import EditorWidget from "@/components/EditView/editors/EditorWidget.vue";
import Translator from "@/utils/common/Translator.ts";
import EditScreenGridColumn = namaTypes.EditScreenGridColumn;
import FieldMetaData = namaTypes.FieldMetaData;

const props = defineProps<{
  params: ICellEditorParams & {
    colId: EditScreenGridColumn["id"],
    columnMetaData: FieldMetaData,
    handler: () => EditScreenGridHandler
  }
}>();

const sqlEditorRef = shallowRef<monacoEditor.editor.IStandaloneCodeEditor>()
const containerRef = useTemplateRef<HTMLDivElement>("containerRef")
const handler = props.params.handler();
const columnMetaData = props.params.columnMetaData
const fieldUtil = new BigTextFieldWidgetUtil(handler.presenter, columnMetaData, props.params);
const colId = props.params.colId || "";

onClickOutside(containerRef, () => props.params.stopEditing())

const isColumnDisabled = (): boolean => {
  return handler.presenter.isColumnDisabled(props.params.data.id, handler.gridId, colId)
}

function onEditorLoaded(editor: monacoEditor.editor.IStandaloneCodeEditor) {
  sqlEditorRef.value = editor;
  editor.onKeyDown((e: IEvent<IKeyboardEvent>) => {
    onKeyDown(e.browserEvent);
  })
}

if (props.params?.eventKey?.length === 1)
  fieldUtil.inputFieldValueRef.value = props.params.eventKey;

function getValue() {
  return fieldUtil.inputFieldValueRef.value;
}

onMounted(() => {
  if (sqlEditorRef.value) {
    setTimeout(() => {
      sqlEditorRef.value!.focus();
      if (props.params?.eventKey?.length === 1)
        return;
    })
  }
});

function namaUpdateValue(value: string) {
  fieldUtil.inputFieldValueRef.value = value;
}

function onKeyDown(event: KeyboardEvent) {
  if (AGGridUtils.isBulkAction(event)) {
    namaUpdateValue(getValue());
    AGGridUtils.dispatchBulkEventFor(containerRef);
  } else if (event.key === 'F2') {
    props.params?.api.stopEditing();
  }
}

defineExpose({getValue, namaUpdateValue});
</script>

<template>
  <div class="tw-flex tw-w-full tw-items-start" ref="containerRef">
    <div :dir="Translator.LTR" class="tw-flex tw-w-full tw-h-64 tw-flex-col tw-gap-2 tw-overflow-hidden">
      <EditorWidget
        fieldSubType="SqlQuery"
        :supportResize="false"
        ref="sqlEditorRef" :isFieldDisabled="isColumnDisabled()"
        @onEditorLoaded="onEditorLoaded" v-model="fieldUtil.inputFieldValueRef.value"/>
    </div>
  </div>
</template>
